import { LoginCredentials, RegisterData, AuthResponse, User } from '../types/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://ai.isdud.com/backend/api/v1';

class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';

  // Get current token from localStorage with enhanced validation
  getToken(): string | null {
    try {
      const token = localStorage.getItem(this.TOKEN_KEY);
      if (!token) return null;
      
      // Basic validation - check if token looks like a JWT
      if (token.length < 10) return null;
      
      // Check if token has expired (basic JWT validation)
      const parts = token.split('.');
      if (parts.length === 3) {
        try {
          const payload = JSON.parse(atob(parts[1]));
          const now = Date.now() / 1000;
          if (payload.exp && payload.exp < now) {
            console.log('Token has expired, clearing auth data');
            this.clearAuthData();
            return null;
          }
        } catch (e) {
          console.error('Error parsing JWT payload:', e);
        }
      }
      
      return token;
    } catch (error) {
      console.error('Error getting token from localStorage:', error);
      return null;
    }
  }

  // Get current user from localStorage with validation
  getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem(this.USER_KEY);
      if (!userStr) return null;
      
      const user = JSON.parse(userStr);
      
      // Basic validation
      if (!user || !user.id) return null;
      
      return user;
    } catch (error) {
      console.error('Error getting user from localStorage:', error);
      return null;
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  // Save auth data to localStorage with enhanced security
  saveAuthData(token: string, user: User): void {
    try {
      if (!token || !user || !user.id) {
        console.error('Invalid auth data provided to saveAuthData');
        return;
      }
      
      // Add timestamp for token validation
      const authData = {
        token,
        user,
        timestamp: Date.now(),
        version: '1.0'
      };
      
      // Store as encrypted JSON (basic obfuscation)
      localStorage.setItem(this.TOKEN_KEY, token);
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
      localStorage.setItem('auth_timestamp', Date.now().toString());
      localStorage.setItem('auth_version', '1.0');
      
      // Verify data was saved correctly
      const savedToken = localStorage.getItem(this.TOKEN_KEY);
      const savedUser = localStorage.getItem(this.USER_KEY);
      const savedTimestamp = localStorage.getItem('auth_timestamp');
      
      if (savedToken !== token || !savedUser || !savedTimestamp) {
        console.error('Failed to save auth data to localStorage');
        this.clearAuthData();
        return;
      }
      
      console.log('Auth data saved successfully');
    } catch (error) {
      console.error('Error saving auth data to localStorage:', error);
      this.clearAuthData();
    }
  }

  // Clear auth data from localStorage with complete cleanup
  clearAuthData(): void {
    try {
      // Clear all auth-related data
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
      localStorage.removeItem('auth_timestamp');
      localStorage.removeItem('auth_version');
      
      // Clear any additional auth-related items
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('auth') || key.includes('token') || key.includes('user'))) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Verify data was cleared
      const remainingItems = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('auth') || key.includes('token') || key.includes('user'))) {
          remainingItems.push(key);
        }
      }
      
      if (remainingItems.length > 0) {
        console.error('Failed to clear all auth data. Remaining items:', remainingItems);
      } else {
        console.log('All auth data cleared successfully');
      }
    } catch (error) {
      console.error('Error clearing auth data from localStorage:', error);
    }
  }

  // Login user
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      console.log('Attempting login to:', `${API_BASE_URL}/auth/login`);
      console.log('Login credentials:', { email: credentials.email, password: '***' });
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });
      
      console.log('Login response status:', response.status);
      console.log('Login response headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Login failed',
          message: data.message || 'Invalid credentials',
        };
      }

      // Backend returns {token, user} for successful login
      if (response.ok && data.token && data.user) {
        this.saveAuthData(data.token, data.user);
        return {
          success: true,
          user: data.user,
          token: data.token,
          message: 'Login successful',
        };
      }

      // If we get here, the response format is unexpected
      return {
        success: false,
        error: data.error || 'Invalid response from server',
      };
    } catch (error: any) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message || 'Network error during login',
      };
    }
  }

  // Register new user
  async register(userData: RegisterData): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Registration failed',
          message: data.message || 'Registration failed',
        };
      }

      // Backend returns {token, user} for successful registration
      if (response.ok && data.token && data.user) {
        this.saveAuthData(data.token, data.user);
        return {
          success: true,
          user: data.user,
          token: data.token,
          message: 'Registration successful',
        };
      }

      // If we get here, the response format is unexpected
      return {
        success: false,
        error: data.error || 'Invalid response from server',
      };
    } catch (error: any) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.message || 'Network error during registration',
      };
    }
  }

  // Logout user
  logout(): void {
    this.clearAuthData();
  }

  // Refresh user data
  async refreshUser(): Promise<User | null> {
    const token = this.getToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        this.clearAuthData();
        return null;
      }

      const data = await response.json();
      // Backend returns {user} for successful profile fetch
      if (response.ok && data.user) {
        localStorage.setItem(this.USER_KEY, JSON.stringify(data.user));
        return data.user;
      }

      return null;
    } catch (error) {
      console.error('Refresh user error:', error);
      this.clearAuthData();
      return null;
    }
  }

  // Update user profile
  async updateProfile(updates: Partial<User>): Promise<AuthResponse> {
    const token = this.getToken();
    if (!token) {
      return {
        success: false,
        error: 'Not authenticated',
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Profile update failed',
        };
      }

      // Backend returns {user} for successful profile update
      if (response.ok && data.user) {
        localStorage.setItem(this.USER_KEY, JSON.stringify(data.user));
        return {
          success: true,
          user: data.user,
          message: 'Profile updated successfully',
        };
      }

      // Backend returns {message} for successful update without user data
      if (response.ok && data.message) {
        // Refresh user data to get updated profile
        const updatedUser = await this.refreshUser();
        return {
          success: true,
          user: updatedUser,
          message: data.message,
        };
      }

      return {
        success: false,
        error: data.error || 'Invalid response from server',
      };
    } catch (error: any) {
      console.error('Profile update error:', error);
      return {
        success: false,
        error: error.message || 'Network error during profile update',
      };
    }
  }

  // Change password
  async changePassword(currentPassword: string, newPassword: string): Promise<AuthResponse> {
    const token = this.getToken();
    if (!token) {
      return {
        success: false,
        error: 'Not authenticated',
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Password change failed',
        };
      }

      // Backend returns {message} for successful password change
      if (response.ok && data.message) {
        return {
          success: true,
          message: data.message,
        };
      }

      return {
        success: false,
        error: data.error || 'Password change failed',
      };
    } catch (error: any) {
      console.error('Password change error:', error);
      return {
        success: false,
        error: error.message || 'Network error during password change',
      };
    }
  }

  // Request password reset
  async requestPasswordReset(email: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Password reset request failed',
        };
      }

      // Backend returns {message} for successful password reset request
      if (response.ok && data.message) {
        return {
          success: true,
          message: data.message,
        };
      }

      return {
        success: false,
        error: data.error || 'Password reset request failed',
      };
    } catch (error: any) {
      console.error('Password reset error:', error);
      return {
        success: false,
        error: error.message || 'Network error during password reset request',
      };
    }
  }

  // Reset password with token
  async resetPassword(token: string, newPassword: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Password reset failed',
        };
      }

      // Backend returns {message} for successful password reset
      if (response.ok && data.message) {
        return {
          success: true,
          message: data.message,
        };
      }

      return {
        success: false,
        error: data.error || 'Password reset failed',
      };
    } catch (error: any) {
      console.error('Password reset error:', error);
      return {
        success: false,
        error: error.message || 'Network error during password reset',
      };
    }
  }
}

export const authService = new AuthService();
